import { useEffect, useState } from 'react';
import { apiFetch } from '../config/api';

const SCHOOL_TYPES = [
  'PUBLIC_DISTRICT', 'PUBLIC_CHARTER', 'PRIVATE_INDEPENDENT',
  'PRIVATE_RELIGIOUS', 'PRIVATE_OTHER', 'PRIVATE_SPECIAL_ED',
  'DAYCARE', 'PREK_CENTER', 'SUMMER_PROGRAM',
];
const TIME_ZONES = ['Eastern', 'Central', 'Mountain', 'Pacific', 'Alaska', 'Hawaii'];
const STATUSES = ['ACTIVE', 'INACTIVE', 'TRIAL', 'SUSPENDED', 'ARCHIVED'];
const BILLING = ['active', 'inactive', 'suspended'];
const PAYMENT = ['month', 'year'];

const EMPTY = {
  legal_name: '', school_nickname: '', type: 'PUBLIC_DISTRICT',
  is_active: 'ACTIVE', time_zone: 'Eastern',
  street_address: '', city: '', state: '', zip_code: '',
  main_phone: '', main_email: '', website_url: '',
  it_email: '', it_phone: '',
  brand_color_primary: '#1a2744', brand_color_secondary: '#4a90d9',
  billing_status: 'active', payment_interval: 'month',
  estimated_student_body: '', estimated_student_staff: '',
  trial_begins: new Date().toISOString().slice(0, 10), trial_ends: '',
  grade_min: '0', grade_max: '8',
};

function buildSchoolForm(school) {
  if (!school) {
    return { ...EMPTY };
  }

  return {
    ...EMPTY,
    ...school,
    trial_begins: school.trial_begins?.slice(0, 10) || '',
    trial_ends: school.trial_ends?.slice(0, 10) || '',
    estimated_student_body: String(school.estimated_student_body || ''),
    estimated_student_staff: String(school.estimated_student_staff || ''),
    grade_min: String(school.grade_min ?? '0'),
    grade_max: String(school.grade_max ?? '8'),
  };
}

export default function SchoolForm({ token, navigate, school, schoolId }) {
  const editSchoolId = school?.id || schoolId || null;
  const isEdit = !!editSchoolId;
  const [schoolRecord, setSchoolRecord] = useState(school || null);
  const [form, setForm] = useState(() => buildSchoolForm(school));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit && !school);

  useEffect(() => {
    if (!school) return;
    setSchoolRecord(school);
    setForm(buildSchoolForm(school));
    setLoading(false);
  }, [school]);

  useEffect(() => {
    if (!isEdit || school || !editSchoolId) return undefined;

    let cancelled = false;
    setLoading(true);

    apiFetch(`/schools/${editSchoolId}`, {}, token)
      .then((res) => {
        if (cancelled) return;
        setSchoolRecord(res.data);
        setForm(buildSchoolForm(res.data));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [editSchoolId, isEdit, school, token]);

  const set = (field) => (e) => setForm((currentForm) => ({ ...currentForm, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const body = {
        ...form,
        estimated_student_body: parseInt(form.estimated_student_body) || 0,
        estimated_student_staff: parseInt(form.estimated_student_staff) || 0,
        grade_min: parseInt(form.grade_min),
        grade_max: parseInt(form.grade_max),
        trial_ends: form.trial_ends || null,
      };

      if (isEdit) {
        await apiFetch(`/schools/${schoolRecord?.id || editSchoolId}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        }, token);
      } else {
        await apiFetch('/schools', {
          method: 'POST',
          body: JSON.stringify(body),
        }, token);
      }
      navigate('dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="sa-empty">Loading...</div>;
  if (isEdit && !schoolRecord) return <div className="sa-error">School not found</div>;

  return (
    <div>
      <div className="sa-breadcrumb">
        <button onClick={() => navigate('dashboard')}>Schools</button>
        {' / '}
        {isEdit ? `Edit: ${schoolRecord?.legal_name || 'School'}` : 'Create School'}
      </div>

      <div className="sa-card">
        <h2>{isEdit ? 'Edit School' : 'Create New School'}</h2>

        {error && <div className="sa-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="sa-form-grid">
            <div className="sa-field">
              <label>Legal Name *</label>
              <input value={form.legal_name} onChange={set('legal_name')} required />
            </div>
            <div className="sa-field">
              <label>Nickname *</label>
              <input value={form.school_nickname} onChange={set('school_nickname')} required />
            </div>

            <div className="sa-field">
              <label>School Type *</label>
              <select value={form.type} onChange={set('type')}>
                {SCHOOL_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className="sa-field">
              <label>Status</label>
              <select value={form.is_active} onChange={set('is_active')}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="sa-field">
              <label>Street Address *</label>
              <input value={form.street_address} onChange={set('street_address')} required />
            </div>
            <div className="sa-field">
              <label>City *</label>
              <input value={form.city} onChange={set('city')} required />
            </div>
            <div className="sa-field">
              <label>State *</label>
              <input value={form.state} onChange={set('state')} maxLength={2} placeholder="FL" required />
            </div>
            <div className="sa-field">
              <label>ZIP Code *</label>
              <input value={form.zip_code} onChange={set('zip_code')} required />
            </div>

            <div className="sa-field">
              <label>Main Phone *</label>
              <input value={form.main_phone} onChange={set('main_phone')} required />
            </div>
            <div className="sa-field">
              <label>Main Email *</label>
              <input type="email" value={form.main_email} onChange={set('main_email')} required />
            </div>
            <div className="sa-field">
              <label>Website URL *</label>
              <input value={form.website_url} onChange={set('website_url')} required />
            </div>
            <div className="sa-field">
              <label>Time Zone *</label>
              <select value={form.time_zone} onChange={set('time_zone')}>
                {TIME_ZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>

            <div className="sa-field">
              <label>IT Email *</label>
              <input type="email" value={form.it_email} onChange={set('it_email')} required />
            </div>
            <div className="sa-field">
              <label>IT Phone *</label>
              <input value={form.it_phone} onChange={set('it_phone')} required />
            </div>

            <div className="sa-field">
              <label>Brand Color Primary *</label>
              <input type="color" value={form.brand_color_primary} onChange={set('brand_color_primary')} />
            </div>
            <div className="sa-field">
              <label>Brand Color Secondary *</label>
              <input type="color" value={form.brand_color_secondary} onChange={set('brand_color_secondary')} />
            </div>

            <div className="sa-field">
              <label>Estimated Student Body *</label>
              <input type="number" value={form.estimated_student_body} onChange={set('estimated_student_body')} required />
            </div>
            <div className="sa-field">
              <label>Estimated Staff *</label>
              <input type="number" value={form.estimated_student_staff} onChange={set('estimated_student_staff')} required />
            </div>

            <div className="sa-field">
              <label>Grade Min *</label>
              <input type="number" min={-3} max={12} value={form.grade_min} onChange={set('grade_min')} required />
            </div>
            <div className="sa-field">
              <label>Grade Max *</label>
              <input type="number" min={-3} max={12} value={form.grade_max} onChange={set('grade_max')} required />
            </div>

            <div className="sa-field">
              <label>Billing Status</label>
              <select value={form.billing_status} onChange={set('billing_status')}>
                {BILLING.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div className="sa-field">
              <label>Payment Interval *</label>
              <select value={form.payment_interval} onChange={set('payment_interval')}>
                {PAYMENT.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            <div className="sa-field">
              <label>Trial Begins *</label>
              <input type="date" value={form.trial_begins} onChange={set('trial_begins')} required />
            </div>
            <div className="sa-field">
              <label>Trial Ends</label>
              <input type="date" value={form.trial_ends} onChange={set('trial_ends')} />
            </div>
          </div>

          <div className="sa-btn-row">
            <button type="submit" className="sa-btn sa-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Update School' : 'Create School'}
            </button>
            <button type="button" className="sa-btn sa-btn-secondary" onClick={() => navigate('dashboard')}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
